import React, { useEffect, useState } from 'react';
import { FieldProps } from '@rjsf/core';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';

/**
 * ClusterMappingSystemPicker
 * 
 * 自定义字段：从 cluster-mapping.yaml 动态加载 system 列表
 * 
 * 使用方式：
 * ```yaml
 * system:
 *   title: 所属系统
 *   type: string
 *   ui:field: ClusterMappingSystemPicker
 * ```
 */
export const ClusterMappingSystemPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
  idSchema,
  schema,
}: FieldProps<string>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  
  const [systems, setSystems] = useState<Array<{ id: string; name: string; description: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    const loadSystems = async () => {
      try {
        setLoading(true);
        setError(undefined);

        const backendUrl = await discoveryApi.getBaseUrl('scaffolder');
        const { token } = await identityApi.getCredentials();

        // 直接获取所有 systems（不再依赖 domain）
        const response = await fetch(
          `${backendUrl}/cluster-mapping`,
          {
            headers: {
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to load systems: ${response.statusText}`);
        }

        const systemsData = await response.json();

        // 转换为选项列表
        const systemList = Object.entries(systemsData).map(([key, value]: [string, any]) => ({
          id: key,
          name: key.toUpperCase(),
          description: value.description || '',
        }));

        setSystems(systemList);
      } catch (err) {
        console.error('Failed to load cluster mapping systems:', err);
        setError(err instanceof Error ? err.message : 'Failed to load systems');
      } finally {
        setLoading(false);
      }
    };

    loadSystems();
  }, [discoveryApi, identityApi]);

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
      fullWidth
    >
      <InputLabel htmlFor={idSchema.$id}>
        {schema.title || 'System'}
      </InputLabel>
      <Select
        id={idSchema.$id}
        value={formData || ''}
        onChange={e => onChange(e.target.value as string)}
        disabled={loading}
      >
        {loading && (
          <MenuItem value="" disabled>
            加载中...
          </MenuItem>
        )}
        {error && (
          <MenuItem value="" disabled>
            加载失败: {error}
          </MenuItem>
        )}
        {!loading && !error && systems.length === 0 && (
          <MenuItem value="" disabled>
            无可用系统
          </MenuItem>
        )}
        {systems.map(system => (
          <MenuItem key={system.id} value={system.id}>
            {system.name} - {system.description}
          </MenuItem>
        ))}
      </Select>
      {rawErrors?.length > 0 && !formData && (
        <FormHelperText error>此字段为必填项</FormHelperText>
      )}
    </FormControl>
  );
};
